import { DataService } from '../../../../services/data_service';
import DialogWrapper2 from '../../../../core/dialog/dialog_wrapper_v2';
import { filterMethodFulltext } from '../../../../core/forms/helpers/filter_method_fulltext';
import formStyle from '../../../../core/forms/style/WForm.less';
import { getFieldKey, setLocaleFieldKeys, toggleI18nFields } from './functions/LocaleFields';
import { IDs } from '../../../../helpers/IDs';
import { i18n } from '../../../../i18n/i18n';
import { i18nLang } from '../../../../i18n/i18nLang';
import { LanguageSelect } from './LanguageSelect';
import { lazyInject } from '../../../../services/inversify';
import parse from 'html-react-parser';
import * as React from 'react';
import { StructureService } from '../../../../services/structure_service';
import { WFormsTableWrapper } from '../../../../core/table/WFormsTableWrapper';
import { WSpinner } from '../../../../core/elements/spinner/WSpinner';

// Copyright TinyEditor ausblenden
// .tox-notifications-container
// .tox-statusbar__branding
// src/main/main.css

export interface MehrsprachigkeitSystemtexteProps {
    admin?: boolean;
    data: any;
    isMultilingual?: boolean;
}

export interface MehrsprachigkeitSystemtexteState {
    data: any[];
    language: string;
    locale: string;
    names: any;
    systemtext: any;
}

export class MehrsprachigkeitSystemtexte extends React.Component<MehrsprachigkeitSystemtexteProps, MehrsprachigkeitSystemtexteState> {

    constructor(props: any) {
        super(props);
        const languageSelected: string = (props.data || {}).LANGUAGE_SELECTED || i18nLang.getUILang();
        this.state = {
            data: null,
            language: languageSelected,
            locale: languageSelected,
            names: null,
            systemtext: null
        };
        this.customFormatter = this.customFormatter.bind(this);
        this.getCustomFormConfigFunc = this.getCustomFormConfigFunc.bind(this);
        this.init = this.init.bind(this);
    }

    @lazyInject(DataService) private dataService: DataService;

    private formatAsTextarea: string[] = [
        'Datenbank Energieeffizienz im Handwerk - Datenschutzerklärung (PDF)'
    ];
    private customColumns: string[] = ['beschreibung'];
    private fieldKeys: any[] = [
        { name: 'name' },
        { name: 'beschreibung' }
    ];
    private fields: any[] = [];
    private hiddenColumns: string[] = [];
    private showingColumns: string[] = [];
    private styleFormField: any = {};

    componentDidMount(): void {
        this.init();
    }

    componentDidUpdate(prevProps: any, prevState: any): void {
        const languageSelected: string = (this.props.data || {}).LANGUAGE_SELECTED || i18nLang.getUILang();

        if (languageSelected !== this.state.locale) {
            this.setState({ data: null, language: languageSelected, locale: languageSelected }, this.init);
        }
    }

    customFormatter(field: string, value: any, data: any, row: any): any {
        return field === 'name' && ((this.state.names || {})[value] || []).length > 1
            ? `${value} (${this.state.names[value].indexOf(data.tid) + 1})`
            : value;
    }

    getCustomActions(): any[] {
        const anzeigen: string = i18n('Anzeigen', 'sys');

        return [
            {
                id: 'showSystemtext',
                label: anzeigen,
                key: 'show',
                func: ((row: any) => this.setState({ systemtext: row })),
                info: anzeigen
            }
        ];
    }

    getCustomColumns(): any[] {
        const beschreibung: string = i18n((this.fields.find((field: any) => field.key === this.fieldKeys[1].locale) || {}).name, 'entity');
        const keyBeschreibung = (data: any): string => getFieldKey(data, this.fieldKeys[1]);

        return [
            {
                accessor: 'beschreibung_table',
                Header: beschreibung,
                HeaderText: beschreibung,
                id: 'beschreibung_table',
                type: 'text',
                filterMethod: (filter: any, row: any) => filterMethodFulltext(filter, row._original, keyBeschreibung(row._original))
            }
        ];
    }

    getCustomFormConfigFunc(formConfig: any, formData: any): void {
        toggleI18nFields(formConfig.items, (formData || {}).values, this.state.language, this.customColumns, (data: any) => {
            const index: number = (formConfig.items || [])
                .findIndex((field: any) => field.key === data.curFields[this.fieldKeys[1].name]);

            if (index === -1) {
                return;
            }
            const isTextarea: boolean = this.formatAsTextarea.indexOf((formData || {}).getValue('name')) !== -1;
            formConfig.items[index].typ = isTextarea ? 'textarea' : 'text';
            formConfig.items[index].format = isTextarea ? 'TEXTAREA' : 'RICHTEXT';
        });
    }

    getExtraFormElements(): any {
        return {
            name:
                <div className={formStyle.fieldBox}>
                    <LanguageSelect
                        label={`${i18n('Sprache', 'sys')}:`}
                        onChange={(language: string) => this.setState({ language })}
                        value={this.state.language}
                    />
                </div>
        };
    }

    getNames(data: any[]): any {
        const names: any = {};

        (data || [])
            .sort((a: any, b: any) => a.tid - b.tid)
            .forEach((item: any) => {
                if (typeof names[item.name] === 'undefined') {
                    names[item.name] = [];
                }
                names[item.name].push(item.tid);
            });

        return names;
    }

    init(): any {
        setLocaleFieldKeys(this.fieldKeys, this.state.locale);
        i18nLang.languages.forEach((language: string) => {
            this.styleFormField[this.fieldKeys[1].name] = { styleTextarea: { height: 350 } };
            this.styleFormField[`${this.fieldKeys[1].name}_${language}`] = { styleTextarea: { height: 350 } };
        });
        const fields: any[] = (((((StructureService || {}).structures || {})[IDs.MOD_ETOOL_ADMIN] || {})[IDs.ENT_SYSTEMTEXTE] || {}).ATTS || [])
            .map((field: any) => ({
                key: field.KEY, ...(field.NAME && { name: field.NAME }), ...(field.HIDDEN && { hidden: field.HIDDEN })
            }));
        toggleI18nFields(fields, null, this.state.language, this.customColumns, (data: any) => {
            data = data || {};
            this.fields = fields;
            this.hiddenColumns = data.hiddenColumns || [];
            this.showingColumns = data.showingColumns || [];
        });
        this.loadData();
    }

    loadData(): void {
        this.dataService.loadData(
            IDs.ENT_SYSTEMTEXTE, {}, {},
            (res: any) => {
                if (Array.isArray(res[IDs.ENT_SYSTEMTEXTE])) {
                    const data = this.processData(res[IDs.ENT_SYSTEMTEXTE]);
                    this.setState({ data, names: this.getNames(data) });
                }
            },
            IDs.MOD_ETOOL_ADMIN
        );
    }

    processData(data: any[]): any[] {
        data.forEach((row: any) => {
            const isTextarea: boolean = this.formatAsTextarea.indexOf(row.name) !== -1;
            const keyBeschreibung: string = getFieldKey(row, this.fieldKeys[1]);
            const keyName: string = getFieldKey(row, this.fieldKeys[0]);
            const beschreibung: string = isTextarea ? row[keyBeschreibung] || '' : parse(row[keyBeschreibung] || '');
            row[this.fieldKeys[0].locale] = row[keyName];
            row.beschreibung_parse = beschreibung;
            row.beschreibung_table = (
                <div style={{ ...(isTextarea && { whiteSpace: 'pre-wrap' }) }}>
                    {beschreibung}
                </div>
            );
        });

        return data;
    }

    render(): JSX.Element {
        if (this.state.data === null) {
            return <WSpinner />
        }

        return <>
            <WFormsTableWrapper
                key={`systemtexte-${this.state.locale}`}
                entity={IDs.ENT_SYSTEMTEXTE}
                data={this.state.data}
                module={IDs.MOD_ETOOL_ADMIN}
                actions={this.props.admin ? ['add', 'edit', 'delete'] : []}
                customColumns={this.getCustomColumns()}
                customActions={this.getCustomActions()}
                actionsBtnReihenfolg={this.props.admin
                    ? ['showSystemtext', 'edit', 'delete']
                    : ['showSystemtext']
                }
                hiddenColumns={this.hiddenColumns}
                showingColumns={this.showingColumns}
                columnWidths={[300]}
                customClass='Custom-ETA'
                formLayout='flowV2'
                customFormConfigFunc={this.getCustomFormConfigFunc}
                customCssStyle={{
                    formDialogWrapper: { width: 960 },
                    formField: this.styleFormField,
                    formFieldRichText: { height: 350 },
                    outerStyle: { minWidth: 950 }
                }}
                customFormatter={this.customFormatter}
                extraFormElementBefore={this.props.isMultilingual ? this.getExtraFormElements() : null}
                onAfterAction={(action: string) => {
                    if (action === 'saveDone' || action === 'delDone') {
                        this.loadData();
                    }
                }}
                tablePageSize={3}
                defaultSorted={[{ id: 'name', asc: true }]}
                hideExport
            />
            {this.renderSystemtext()}
        </>;
    }

    renderSystemtext(): JSX.Element {
        const systemtext: any = this.state.systemtext || {};

        if (!Object.keys(systemtext).length) {
            return null;
        }
        const isTextarea: boolean = this.formatAsTextarea.indexOf((systemtext || {}).name) !== -1;

        return (
            <DialogWrapper2
                width={900}
                height={600}
                title={systemtext.name}
                onAfterClose={() => this.setState({ systemtext: null })}
            >
                <div style={{ padding: '20px 50px 20px 50px', ...(isTextarea && { whiteSpace: 'pre-wrap' }) }}>
                    {systemtext.beschreibung_parse}
                </div>
            </DialogWrapper2>
        );
    }
}
